/**
 * MathLive wrapped for React (ADR-0007).
 *
 * A custom element inside React 18 needs care in three places, and each one is a
 * real bug if skipped:
 *
 * 1. **React sets attributes, not properties.** `value` on `<math-field>` is a
 *    property; passing it as a JSX prop stringifies it into an attribute that
 *    MathLive reads only once, so the field stops tracking state. Set it on the
 *    element through a ref instead.
 * 2. **Writing `value` while the field has focus moves the caret to the end.** So a
 *    write is skipped whenever the incoming value already matches what the user
 *    typed -- otherwise every keystroke jumps the cursor.
 * 3. **The element must exist before the first write.** Registration is a module
 *    side effect, so it is imported here rather than at the app root, where a lazy
 *    route could load the editor before it ran.
 *
 * Fonts are vendored by `yarn mathlive` and served from our own origin (3.1).
 */
import { useEffect, useRef } from 'react'
import { MathfieldElement } from 'mathlive'

import { cn } from '@/lib/cn'

// Vendored, never a CDN: the deployment runs under `font-src 'self'`.
MathfieldElement.fontsDirectory = '/mathlive/fonts'
// The sounds are a keypress click. Silence is the right default in an office.
MathfieldElement.soundsDirectory = null

export interface MathFieldProps {
  readonly value: string
  readonly onChange: (latex: string) => void
  readonly onFocus?: () => void
  readonly placeholder?: string
  readonly readOnly?: boolean
  readonly className?: string
  readonly ariaLabel: string
}

export function MathField({
  value,
  onChange,
  onFocus,
  placeholder,
  readOnly = false,
  className,
  ariaLabel,
}: MathFieldProps) {
  const host = useRef<HTMLDivElement | null>(null)
  const field = useRef<MathfieldElement | null>(null)
  // What we last saw from the user, so an echo of our own value is not written back.
  const lastEmitted = useRef(value)

  useEffect(() => {
    const container = host.current
    if (!container) return

    const element = new MathfieldElement({
      // No shortcut should silently rewrite what an engineer typed. `:=` in
      // particular must survive as itself, since the parser keys off it.
      smartMode: false,
      smartFence: true,
      removeExtraneousParentheses: false,
    })

    element.readOnly = readOnly
    element.setAttribute('aria-label', ariaLabel)
    if (placeholder) element.setAttribute('placeholder', placeholder)
    element.style.width = '100%'
    element.style.fontSize = '19px'
    element.value = value
    lastEmitted.current = value

    const handleInput = () => {
      lastEmitted.current = element.value
      onChange(element.value)
    }
    const handleFocus = () => onFocus?.()

    element.addEventListener('input', handleInput)
    element.addEventListener('focusin', handleFocus)
    container.appendChild(element)
    field.current = element

    return () => {
      element.removeEventListener('input', handleInput)
      element.removeEventListener('focusin', handleFocus)
      element.remove()
      field.current = null
    }
    // Built once per cell. Prop changes are applied by the effects below rather
    // than by tearing the field down, which would lose the caret on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const element = field.current
    if (!element) return
    // Only write a value that did not come from this field, or the caret jumps to
    // the end on every keystroke.
    if (value !== lastEmitted.current) {
      element.value = value
      lastEmitted.current = value
    }
  }, [value])

  useEffect(() => {
    if (field.current) field.current.readOnly = readOnly
  }, [readOnly])

  return <div ref={host} className={cn('min-w-0 flex-1', className)} />
}

/** Focuses the field inside `container`, for "click the row, edit the math". */
export function focusMathField(container: HTMLElement | null): void {
  const element = container?.querySelector('math-field')
  if (element instanceof MathfieldElement) element.focus()
}
